import { z } from 'zod'

// =====================
// Meeting Recording Enums
// =====================

export enum MeetingRecordingSourceEnum {
  DESKTOP_CAPTURE = 'DESKTOP_CAPTURE',
  BOT = 'BOT',
}

export enum MeetingRecordingStatusEnum {
  UPLOADING = 'UPLOADING',
  UPLOADED = 'UPLOADED',
  TRANSCRIBING = 'TRANSCRIBING',
  SUMMARIZING = 'SUMMARIZING',
  READY = 'READY',
  FAILED = 'FAILED',
}

export enum MeetingRecordingErrorStageEnum {
  UPLOAD = 'UPLOAD',
  TRANSCRIBE = 'TRANSCRIBE',
  SUMMARIZE = 'SUMMARIZE',
}

// =====================
// Zod Schemas
// =====================

export const MeetingRecordingSourceSchema = z.nativeEnum(
  MeetingRecordingSourceEnum,
)
export const MeetingRecordingStatusSchema = z.nativeEnum(
  MeetingRecordingStatusEnum,
)

export const TranscriptSegmentSchema = z.object({
  start: z.number(), // seconds
  end: z.number(),
  text: z.string(),
  speaker: z.string().optional().nullable(),
})

export const MeetingRecordingSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  createdBy: z.string().uuid(),
  source: MeetingRecordingSourceSchema.default(
    MeetingRecordingSourceEnum.DESKTOP_CAPTURE,
  ),
  status: MeetingRecordingStatusSchema.default(
    MeetingRecordingStatusEnum.UPLOADING,
  ),
  title: z.string().max(500).optional().nullable(),
  audioKey: z.string().optional().nullable(),
  documentId: z.string().uuid().optional().nullable(),
  mimeType: z.string().max(100).optional().nullable(),
  fileSize: z.number().optional().nullable(), // BigInt serialized as number
  durationSeconds: z.number().optional().nullable(),
  recordedAt: z.date().optional().nullable(),
  eventId: z.string().uuid().optional().nullable(),
  noteId: z.string().uuid().optional().nullable(),
  moduleType: z.string().optional().nullable(),
  moduleId: z.string().uuid().optional().nullable(),
  errorMessage: z.string().optional().nullable(),
  errorStage: z
    .nativeEnum(MeetingRecordingErrorStageEnum)
    .optional()
    .nullable(),
  attemptCount: z.number().default(0),
  processingStartedAt: z.date().optional().nullable(),
  completedAt: z.date().optional().nullable(),
  metadata: z.record(z.any()).default({}),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().optional().nullable(),
})

export const MeetingTranscriptSchema = z.object({
  id: z.string().uuid(),
  recordingId: z.string().uuid(),
  engine: z.string().max(50),
  language: z.string().max(10).optional().nullable(),
  fullText: z.string(),
  segments: z.array(TranscriptSegmentSchema).default([]),
  // Raw diarization label → display name ("spk_0" → "Joel Thomas");
  // AI-inferred during summarization, editable by the user afterwards
  speakers: z.record(z.string()).default({}),
  wordCount: z.number().optional().nullable(),
  createdAt: z.date(),
})

// =====================
// Create/Update DTOs
// =====================

export const CreateMeetingRecordingSchema = z.object({
  fileName: z.string().min(1).max(255),
  mimeType: z
    .string()
    .max(100)
    .regex(/^audio\//, 'Only audio files are accepted'),
  fileSize: z.number().int().positive(),
  durationSeconds: z.number().int().positive().optional(),
  recordedAt: z.coerce.date().optional(),
  title: z.string().max(500).optional(),
  eventId: z.string().uuid().optional(),
  moduleType: z.string().optional(),
  moduleId: z.string().uuid().optional(),
  metadata: z.record(z.any()).optional(),
})

export const MeetingRecordingPartUrlsSchema = z.object({
  partNumbers: z.array(z.number().int().min(1).max(10000)).min(1).max(100),
})

export const CompleteMeetingRecordingSchema = z.object({
  parts: z
    .array(
      z.object({
        partNumber: z.number().int().min(1).max(10000),
        etag: z.string().min(1),
      }),
    )
    .min(1),
  durationSeconds: z.number().int().positive().optional(),
})

export const UpdateMeetingRecordingSchema = z.object({
  title: z.string().max(500).optional(),
  moduleType: z.string().optional().nullable(),
  moduleId: z.string().uuid().optional().nullable(),
  eventId: z.string().uuid().optional().nullable(),
})

export const UpdateMeetingSpeakersSchema = z.object({
  // Full replacement map: raw label → display name
  speakers: z.record(z.string().min(1).max(200)),
})

// =====================
// API Response Types
// =====================

export const CreateMeetingRecordingResponseSchema = z.object({
  recordingId: z.string().uuid(),
  key: z.string(),
  uploadId: z.string(),
  partSize: z.number(),
})

export const MeetingRecordingAudioUrlSchema = z.object({
  url: z.string(),
  mimeType: z.string().nullable(),
  durationSeconds: z.number().nullable(),
  expiresAt: z.string(), // ISO timestamp; refetch after expiry
})

// =====================
// Inferred Types
// =====================

export type MeetingRecordingType = z.infer<typeof MeetingRecordingSchema>
export type MeetingTranscriptType = z.infer<typeof MeetingTranscriptSchema>
export type TranscriptSegmentType = z.infer<typeof TranscriptSegmentSchema>
export type CreateMeetingRecordingType = z.infer<
  typeof CreateMeetingRecordingSchema
>
export type MeetingRecordingPartUrlsType = z.infer<
  typeof MeetingRecordingPartUrlsSchema
>
export type CompleteMeetingRecordingType = z.infer<
  typeof CompleteMeetingRecordingSchema
>
export type UpdateMeetingRecordingType = z.infer<
  typeof UpdateMeetingRecordingSchema
>
export type UpdateMeetingSpeakersType = z.infer<
  typeof UpdateMeetingSpeakersSchema
>
export type CreateMeetingRecordingResponseType = z.infer<
  typeof CreateMeetingRecordingResponseSchema
>
export type MeetingRecordingAudioUrlType = z.infer<
  typeof MeetingRecordingAudioUrlSchema
>

// =====================
// Summary templates
// =====================

/** Reference stored on a recording (`metadata.summaryTemplate`):
 *  a built-in key ('sales_call') or an org custom template ('custom:<uuid>'). */
export const CUSTOM_TEMPLATE_PREFIX = 'custom:'

export interface MeetingSummaryTemplateDef {
  key: string
  name: string
  description: string
  /** Appended to the summarizer prompt to shape the notes for this meeting type */
  instructions: string
}

export const MEETING_SUMMARY_TEMPLATES: MeetingSummaryTemplateDef[] = [
  {
    key: 'sales_call',
    name: 'Sales call',
    description: 'Prospect needs, objections, commercials, next steps',
    instructions:
      'This is a sales call. Structure summary_sections as: "Prospect & context" (company, attendees, how the call came about), ' +
      '"Needs & pain points", "Product discussion & objections" (what resonated, every objection and how it was answered), ' +
      '"Commercials" (budget, pricing discussed, timeline, decision process/makers — note explicitly when not discussed), ' +
      'and "Next steps". Always capture competitor mentions verbatim. Action items should focus on follow-ups owed to the prospect.',
  },
  {
    key: 'standup',
    name: 'Standup',
    description: 'Per-person progress, plans, and blockers',
    instructions:
      'This is a team standup. Keep the summary tight. Structure summary_sections as one section per participant with three bullets ' +
      '(Done / Next / Blockers), followed by a "Blockers & risks" section aggregating anything that needs escalation. ' +
      'Action items should be blockers needing follow-up, not routine planned work.',
  },
  {
    key: 'one_on_one',
    name: '1:1',
    description: 'Topics, feedback both ways, growth, commitments',
    instructions:
      'This is a 1:1 conversation. Structure summary_sections as: "Topics discussed", "Feedback" (in both directions, attributed), ' +
      '"Growth & career" (skip if not discussed), and "Commitments" (who agreed to what). Keep a neutral, private tone — ' +
      'this summary is personal working notes, not a broadcast. Do not editorialize on sensitive topics.',
  },
  {
    key: 'interview',
    name: 'Interview',
    description:
      'Candidate background, signals, concerns, recommendation inputs',
    instructions:
      'This is a job interview. Structure summary_sections as: "Candidate background" (experience relevant to the role), ' +
      '"Strengths & signals" (with the concrete evidence/answers behind each), "Concerns & gaps" (only what the conversation supports), ' +
      'and "Candidate questions" (what they asked — it signals what they care about). Quote key answers rather than paraphrasing loosely. ' +
      'Do NOT output a hire/no-hire recommendation — capture evidence, not verdicts. Action items are process follow-ups (references, next rounds, feedback to submit).',
  },
]

export function getBuiltinTemplate(
  key: string,
): MeetingSummaryTemplateDef | undefined {
  return MEETING_SUMMARY_TEMPLATES.find(t => t.key === key)
}

// Org custom template (API shape)
export const MeetingSummaryTemplateSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  createdBy: z.string().uuid().optional().nullable(),
  name: z.string().max(100),
  instructions: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export const CreateMeetingSummaryTemplateSchema = z.object({
  name: z.string().min(1).max(100),
  instructions: z.string().min(1).max(4000),
})

export const MeetingTemplatePreferenceSchema = z.object({
  eventKey: z.string().max(255),
  templateRef: z.string().max(120).nullable(), // null clears the default
})

export type MeetingSummaryTemplateType = z.infer<
  typeof MeetingSummaryTemplateSchema
>
export type CreateMeetingSummaryTemplateType = z.infer<
  typeof CreateMeetingSummaryTemplateSchema
>
export type MeetingTemplatePreferenceType = z.infer<
  typeof MeetingTemplatePreferenceSchema
>

// Structured output of the AI summarizer step.
// Markdown fields may contain [[t:HH:MM:SS]] citation markers pointing at the
// supporting transcript moment; the note builder converts them to links.
export interface MeetingSummaryResult {
  title: string
  summarySections: { heading: string; markdown: string }[]
  decisions: string[]
  actionItems: {
    description: string
    assigneeHint?: string | null
    dueDateHint?: string | null
  }[]
  // Diarization label → inferred participant name (only labels the model
  // could confidently identify from the conversation/attendee hints)
  speakers?: Record<string, string>
}

// =====================
// Launch readiness: consent, plan gating, credits
// =====================

/**
 * Version of the recording-consent acknowledgement. Bump it whenever the
 * copy below changes materially — every user then re-acknowledges before
 * their next recording (the API rejects uploads for a stale version).
 *
 * Approved by legal 2026-09-26 (docs/PLAN-meeting-recorder-launch.md).
 */
export const MEETING_RECORDING_CONSENT_VERSION = '2026-09-1'

export const MEETING_RECORDING_CONSENT_COPY = {
  version: MEETING_RECORDING_CONSENT_VERSION,
  title: 'Before you record meetings',
  intro:
    'Scrambl records meeting audio from your device and turns it into a transcript and notes.',
  points: [
    'You are responsible for telling everyone in the meeting that it is being recorded, and for getting their consent where the law requires it. Some states and countries require consent from every participant.',
    "Don't record if a participant objects or if recording isn't allowed.",
    "On the Mac desktop app, Scrambl records your microphone and your computer's audio, so other participants are captured. On Windows, in a browser, and on mobile, only your microphone is recorded.",
    'Recordings, transcripts, and notes are stored in this workspace. Deleting a recording permanently removes its audio and transcript.',
  ],
  acknowledgeLabel:
    "I'll let participants know and get their consent where required",
  confirmLabel: 'Continue',
} as const

/** Text the "Copy notice to meeting chat" helper puts on the clipboard. */
export const MEETING_RECORDING_CHAT_NOTICE =
  "Heads up: I'm recording this meeting with Scrambl to take notes."

/** Machine-readable `code` values on meeting-recorder API errors. */
export const MEETING_RECORDER_ERROR_CODES = {
  /** 409 — the user hasn't acknowledged the current consent version. */
  CONSENT_REQUIRED: 'MEETING_RECORDING_CONSENT_REQUIRED',
  /** 403 — the org's plan doesn't include `meeting_recorder` (Pro+). */
  NOT_ENTITLED: 'MEETING_RECORDER_NOT_ENTITLED',
  /** 403 — the `meeting_notes` flag is off, or this is a personal space. */
  NOT_ENABLED: 'MEETING_NOTES_NOT_ENABLED',
  /** 429 — not enough AI credits to summarize (transcription is free). */
  INSUFFICIENT_CREDITS: 'MEETING_NOTES_INSUFFICIENT_CREDITS',
} as const

export type MeetingRecorderErrorCode =
  (typeof MEETING_RECORDER_ERROR_CODES)[keyof typeof MEETING_RECORDER_ERROR_CODES]

/** hidden = no recorder UI at all; locked = neutral locked state (plan);
 *  available = recording may start (consent may still be required). */
export type MeetingRecorderAvailability = 'hidden' | 'locked' | 'available'

export type MeetingRecorderUnavailableReason =
  | 'personal'
  | 'flag_off'
  | 'surface_off'
  | 'plan'

export interface MeetingRecorderAvailabilityResult {
  availability: MeetingRecorderAvailability
  reason: MeetingRecorderUnavailableReason | null
}

/**
 * Single decision table for every recorder surface (web, desktop, mobile,
 * API readiness). Personal spaces are always hidden — the subscription layer
 * is inert for them, so entitlement alone would wrongly open the recorder.
 */
export function resolveMeetingRecorderAvailability(input: {
  isPersonal: boolean
  isFlagEnabled: boolean
  isEntitled: boolean
  /** Extra per-surface switch (e.g. the mobile entry flag). Defaults on. */
  isSurfaceEnabled?: boolean
}): MeetingRecorderAvailabilityResult {
  if (input.isPersonal) return { availability: 'hidden', reason: 'personal' }
  if (!input.isFlagEnabled)
    return { availability: 'hidden', reason: 'flag_off' }
  if (input.isSurfaceEnabled === false) {
    return { availability: 'hidden', reason: 'surface_off' }
  }
  if (!input.isEntitled) return { availability: 'locked', reason: 'plan' }
  return { availability: 'available', reason: null }
}

export const MeetingRecordingConsentSurfaceSchema = z.enum([
  'web',
  'desktop',
  'mobile',
])

export const AcknowledgeMeetingRecordingConsentSchema = z.object({
  version: z.string().min(1).max(64),
  surface: MeetingRecordingConsentSurfaceSchema,
})

export type MeetingRecordingConsentSurface = z.infer<
  typeof MeetingRecordingConsentSurfaceSchema
>
export type AcknowledgeMeetingRecordingConsentType = z.infer<
  typeof AcknowledgeMeetingRecordingConsentSchema
>

export interface MeetingRecordingConsentStatusType {
  /** Version the user must acknowledge (MEETING_RECORDING_CONSENT_VERSION). */
  version: string
  /** True until the user acknowledges `version` in this workspace. */
  isRequired: boolean
  /** When the current version was acknowledged (ISO); null when required. */
  acknowledgedAt: string | null
}

/** GET /meeting-recorder/readiness — checked BEFORE capture starts so no
 *  audio is recorded that the API would later refuse. */
export interface MeetingRecorderReadinessType extends MeetingRecorderAvailabilityResult {
  consent: MeetingRecordingConsentStatusType
  /** Summary credit status; null when the recorder isn't available. */
  credits: {
    canSummarize: boolean
    remainingCredits: number
    requiredCredits: number
  } | null
}
